import React, { useState, useRef } from 'react';
import {
  Flex,
  Text,
  ActionNote,
  StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1QuestionsWithCounty,
  allowanceAndAdditionalAmt,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Basic Info Questions
  const step1Form = useRef();
  const step1InitQ = getInitialQuestions(step1QuestionsWithCounty);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitQ);

  // Allowances and Additional Amount Questions
  const allowancesAndAdditionalAmtForm = useRef();
  const allowancesAndAdditionalAmtInitQ = getInitialQuestions(allowanceAndAdditionalAmt);
  const [allowancesAndAdditionalAmtQuestions, setAllowancesAndAdditionalAmtQuestions] = useState(
    allowancesAndAdditionalAmtInitQ,
  );

  const handleValidation = () => {
    if (step1Form && step1Form.current && step1Form.current.validate((error) => !!error)) {
      return true;
    }

    if (allowancesAndAdditionalAmtForm && allowancesAndAdditionalAmtForm.current
    && allowancesAndAdditionalAmtForm.current.validate((error) => !!error)) {
      return true;
    }

    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();
    if (hasErrors) return;

    const stepOneData = stepOneQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    const allowancesAndAdditionalAmtData = allowancesAndAdditionalAmtQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    const payload = {
      ...stepOneData,
      ...allowancesAndAdditionalAmtData,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  // render form from step 1 (fill in form)
  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderAllowancesAndAdditionalAmt = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={allowancesAndAdditionalAmtQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesAndAdditionalAmtQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesAndAdditionalAmtQuestions(questions);
        }}
        ref={allowancesAndAdditionalAmtForm}
      />
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderAllowancesAndAdditionalAmt()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderAllowancesAndAdditionalAmt(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
